import { trim } from 'lodash-es'

import { Path, PropsWithClassName } from './types'

/** Flattens plain objects. */
export const toDotNotation = <V extends object, RecordValue extends never>(
  obj: V
) => {
  type Alias = Path<V>
  type ValueKey = keyof V

  const result = {} as Record<Alias, RecordValue>

  for (const key of Object.keys(obj)) {
    const value = obj[key as ValueKey]

    if (typeof value === 'object' && value !== null) {
      type NestedKey = keyof typeof nested

      const nested = toDotNotation<typeof value, RecordValue>(value)

      for (const nestedKey of Object.keys(nested)) {
        const alias = `${key}.${nestedKey}` as Alias
        const value = nested[nestedKey as NestedKey]

        result[alias] = value
      }
    } else {
      result[key as Alias] = value as RecordValue
    }
  }

  return result
}

export const toClassNames = (...args: (string | undefined)[]) => {
  // @ts-expect-error `undefined` is already filtered out
  return args.filter(Boolean).map(trim).join(' ')
}

/**
 * *Example usage:*
 * ```tsx
 * const props = { className: 'original' }
 * const otherClass1 = style({ color: 'white' })
 * const otherClass2 = style({ backgroundColor: 'red' })
 *
 * <div {...propsWithClassNames(props, otherClass1, otherClass2)} />
 * // same as the following 👇
 * <div {...props} className={`${otherClass1} ${otherClass2} ${props.className}`}  />
 * ```
 */

export const propsWithClassNames = <T extends PropsWithClassName | undefined>(
  /** Props containing original `className`. */
  props: T,

  /** List of `className` to be injected before the original `className`. */
  ...classNames: Parameters<typeof toClassNames>
) => ({
  ...props,
  className: toClassNames(...classNames, props?.className)
})

export const trueOrUndefined = <T>(value: T) => (value ? true : undefined)
